import type { Entry } from '../../../features/admin/modules/modules.model';
import { someInFieldTree } from '../formly-fields/field-tree.util';
import type { PageKeySource, ReviewFlag, WizardPageFieldTree, WizardPageVisibility } from './review-flags.types';

const PAGE_TARGET_PREFIX = 'page:';

export function pageTarget(pageKey: string): string {
  return `${PAGE_TARGET_PREFIX}${pageKey}`;
}

/**
 * The removed Aanvrager-section target. Still persisted on older reviews, rendered nowhere.
 * Module-private: only {@link isDocumentTarget} has to know the name, and an export nothing
 * imported invited a second, drifting copy of the string elsewhere.
 */
const LEGACY_AANVRAGER_TARGET = 'aanvrager';

/**
 * Whether a flag addresses a document record rather than a wizard page. There is no id format to
 * lean on — a document target is simply a record id — so this is decided by elimination: not a
 * `page:` target, and not the one legacy target that survives on old reviews. A future non-document
 * target would have to be excluded here as well, which is exactly the point of naming the legacy
 * one instead of silently treating every unknown target as a document.
 */
export function isDocumentTarget(target: string): boolean {
  return !target.startsWith(PAGE_TARGET_PREFIX) && target !== LEGACY_AANVRAGER_TARGET;
}

/**
 * The identity a wizard page is flagged under. One derivation, because a target that differs by so
 * much as an empty string would make a flag address nothing without any error.
 *
 * The key wins whenever there is one — a page with a key is addressed by it, whatever its label
 * says, including an empty label. An empty or absent key falls through to the label, and a page
 * offering neither yields '' rather than the string "undefined".
 */
export function pageKeyOf(page: PageKeySource | null | undefined): string {
  return asKeyPart(page?.key) || asKeyPart(page?.label) || asKeyPart(page?.props?.label);
}

/**
 * Whether a wizard page is part of the stepper the user walks through. Both the status page and the
 * wizard itself index into that list — the wizard to resolve a start step, the status page to name
 * the steps — and a page counted by one but not the other puts them one step out of sync.
 */
export function isVisibleWizardPage(page: WizardPageVisibility | null | undefined): boolean {
  return !!page && page.hide !== true && page.className !== 'hidden';
}

/** The pages of a wizard in stepper order, hidden ones dropped. See {@link isVisibleWizardPage}. */
export function visibleWizardPages<T extends WizardPageVisibility>(pages: readonly T[] | null | undefined): T[] {
  return (pages ?? []).filter(page => isVisibleWizardPage(page));
}

/** Formly field type of the checklist that owns the carrier's document rows. */
export const DOCUMENTS_CHECKLIST_FIELD_TYPE = 'required-documents-checklist';

/**
 * Whether a wizard page renders the required-documents checklist, and is therefore the step a
 * DOCUMENT flag belongs to. Document flags carry a record id as target (see {@link isDocumentTarget})
 * and can never name a page, so the page has to be found by what it contains.
 *
 * Shared so the status page and the wizard cannot disagree about WHICH step that is: the status page
 * sends the carrier there ("Aanpassen" on a document row) and the wizard is what marks it as
 * flagged. Walks the whole subtree rather than only the page's direct children — a checklist nested
 * one field group deep is still on that page. See `field-tree.util.ts` for the shared walk.
 */
export function hostsDocumentsChecklist(page: WizardPageFieldTree | null | undefined): boolean {
  return someInFieldTree(page ? [page] : null, field => field.type === DOCUMENTS_CHECKLIST_FIELD_TYPE);
}

function asKeyPart(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

function isReviewFlag(value: unknown): value is ReviewFlag {
  if (!value || typeof value !== 'object') return false;
  const flag = value as Record<string, unknown>;
  return typeof flag['target'] === 'string' && typeof flag['label'] === 'string' && typeof flag['reason'] === 'string';
}

/**
 * Flags persisted INSIDE the review field's own value — `entry.<fieldKey>.flags`, where fieldKey
 * is the key of the `readonly_onboarding` field rendering the review (`review` in the current
 * config). The onboarding LINK lives in a sibling key of its own and is never read from here.
 * Keeping them there makes flagging a self-contained option of that field: no module needs a
 * separate `flags` field, and a module that still has one is simply not read from any more.
 * Anything that is not a well-formed flag is dropped.
 */
export function readFlags(entry: Entry | null | undefined, fieldKey: string): ReviewFlag[] {
  const ownValue = fieldKey ? entry?.entry?.[fieldKey] : undefined;
  const raw = ownValue && typeof ownValue === 'object' ? (ownValue as { flags?: unknown }).flags : undefined;
  return sanitizeFlags(raw);
}

/**
 * Anything that is not a well-formed flag is dropped; a non-array reads as no flags at all.
 * `resolved` is copied through only when it is really a boolean, so a flag that never carried the
 * key keeps its exact three-key shape — the persisted list stays free of `resolved: undefined`.
 */
function sanitizeFlags(raw: unknown): ReviewFlag[] {
  return !Array.isArray(raw)
    ? []
    : raw.filter(isReviewFlag).map(flag => ({
        target: flag.target,
        label: flag.label,
        reason: flag.reason,
        ...(typeof flag.resolved === 'boolean' ? { resolved: flag.resolved } : {}),
      }));
}

/**
 * The flags that still ask the carrier for something. EVERYTHING that acts on flags counts these
 * and only these: `flagCount` (which gates the Goedkeuren interface action), the composed decline
 * text, the document-rejection loop at deny, and every carrier-facing reader. A resolved flag is
 * reviewer-only history until it is reopened.
 */
export function openFlags(flags: readonly ReviewFlag[]): ReviewFlag[] {
  return flags.filter(flag => !flag.resolved);
}

/**
 * Mark every flag as resolved, keeping label and reason. Written once per resubmit by the review
 * field: the carrier has acted on the whole round, so nothing is left open and Goedkeuren becomes
 * available without the reviewer having to clear the list by hand first.
 */
export function resolveFlags(flags: readonly ReviewFlag[]): ReviewFlag[] {
  return flags.map(flag => ({ ...flag, resolved: true }));
}

/**
 * Default key on the carrier's onboarding entry that action 9.0 fills with the review flags at
 * deny, and the default status slug that makes those flags visible. Both are only the DEFAULTS of
 * {@link readCarrierFlags}'s parameters — this file is shared infrastructure, so a second surface
 * (or a spec) can read flags stored under its own key and slug without editing it.
 */
export const REVIEW_FLAGS_ENTRY_KEY = 'review-flags';
/** See {@link REVIEW_FLAGS_ENTRY_KEY}. */
export const DENIED_STATUS_SLUG = 'afgekeurd';

/**
 * Flags stored as a plain list under `entry.<key>` (the carrier-side copy written by action 9.0),
 * as opposed to the review field's nested `<fieldKey>.flags`. Same sanitising as `readFlags`.
 *
 * Resolved flags are dropped here rather than at every call site: 9.0 mirrors the WHOLE array, so
 * a round the carrier already reworked would otherwise come back as a live remark on the status
 * card and a dot on the wizard step — asking again for something that was already done.
 */
export function readEntryFlags(entry: Entry | null | undefined, key: string): ReviewFlag[] {
  return openFlags(sanitizeFlags(key ? entry?.entry?.[key] : undefined));
}

/**
 * The flags the carrier may see: only while the request is denied. The carrier side never writes
 * them — the admin clears them on the aanmelding after the next review.
 *
 * `entryKey` and `deniedSlug` are parameters rather than baked-in constants so this shared util
 * carries no hidden coupling to one module's storage key or one tenant's Dutch status vocabulary;
 * every current caller takes the defaults.
 */
export function readCarrierFlags(
  entry: Entry | null | undefined,
  entryKey: string = REVIEW_FLAGS_ENTRY_KEY,
  deniedSlug: string = DENIED_STATUS_SLUG
): ReviewFlag[] {
  const slug = (entry?.entry?.['status'] as { slug?: string } | undefined)?.slug;
  return slug === deniedSlug ? readEntryFlags(entry, entryKey) : [];
}

export function flagForTarget(flags: readonly ReviewFlag[], target: string): ReviewFlag | null {
  return flags.find(flag => flag.target === target) ?? null;
}

/** What the carrier reads for a flag: the reason, or the label when the reviewer typed none. */
export function flagNoticeText(flag: ReviewFlag): string {
  return flag.reason.trim() || flag.label;
}

/** Whether the target carries an OPEN flag — a resolved one is history, not a marking. */
export function isFlagged(flags: readonly ReviewFlag[], target: string): boolean {
  const flag = flagForTarget(flags, target);
  return !!flag && !flag.resolved;
}

/** Whether the target carries a flag the carrier already resubmitted on. */
export function isFlagResolved(flags: readonly ReviewFlag[], target: string): boolean {
  return !!flagForTarget(flags, target)?.resolved;
}

/** The reason of the target's flag, open or resolved — a resolved row still shows what was asked. */
export function flagReason(flags: readonly ReviewFlag[], target: string): string {
  return flagForTarget(flags, target)?.reason ?? '';
}

/**
 * Prefilled reason for a freshly marked wizard section. It is written as the flag's reason (so
 * it reaches the decline text, the carrier's status card and the wizard notice unchanged) and
 * the reviewer edits it in the panel. Reads naturally after the "<label>: " prefix `flagLine`
 * adds, and stays well under the panel's 120-character cap.
 */
export const DEFAULT_SECTION_REASON =
  'De gegevens in deze stap zijn onvolledig of onjuist. Controleer ze en pas ze aan.';

/** Prefilled reason for a freshly marked document, phrased for the record's current status. */
export function defaultDocumentReason(status: 'approved' | 'rejected' | 'expired' | 'pending'): string {
  return status === 'expired'
    ? 'Dit document is verlopen. Upload een geldige, actuele versie.'
    : 'Dit document is onvolledig of onjuist. Upload een nieuwe versie.';
}

/**
 * The three states of one click, in the order the reviewer meets them: no flag → mark it (with
 * `initialReason` prefilled, so the carrier never receives a bare label); open flag → remove it;
 * resolved flag → REOPEN it, keeping the reason that was typed last round (the reviewer is asking
 * for the same thing again, and retyping it is the one thing he should not have to do). The label
 * is refreshed from the caller, which reads it off the live row.
 */
export function toggleFlag(
  flags: readonly ReviewFlag[],
  target: string,
  label: string,
  initialReason = ''
): ReviewFlag[] {
  const existing = flags.find(flag => flag.target === target);
  if (!existing) return [...flags, { target, label, reason: initialReason }];
  if (existing.resolved)
    return flags.map(flag => (flag.target === target ? { ...flag, label, resolved: false } : flag));
  return flags.filter(flag => flag.target !== target);
}

/**
 * Set the reason on an existing flag. Editing the reason of a resolved flag reopens it: the
 * reviewer is writing a new instruction, which is meaningless on a marking that counts for
 * nothing. `resolved` is only touched when it was actually set, so an ordinary reason edit keeps
 * the flag's three-key shape.
 */
export function setFlagReason(flags: readonly ReviewFlag[], target: string, reason: string): ReviewFlag[] {
  return flags.map(flag =>
    flag.target === target ? (flag.resolved ? { ...flag, reason, resolved: false } : { ...flag, reason }) : flag
  );
}

/** One flag as one line: "<label>: <reason>", or just "<label>" when the reviewer typed none. */
function flagLine(flag: ReviewFlag): string {
  const reason = flag.reason.trim();
  return reason ? `${flag.label}: ${reason}` : flag.label;
}

/** One line per flag in flag order, joined with `separator`. See {@link flagLine}. */
export function composeFlagLines(flags: readonly ReviewFlag[], separator: string): string {
  return flags.map(flagLine).join(separator);
}

/**
 * One line per OPEN flag, in flag order: "<label>: <reason>", or just "<label>" without a reason.
 * Resolved flags are left out — this text is the denial the carrier reads, and it must only list
 * what is still being asked of him.
 */
export function composeDeclineMessage(flags: readonly ReviewFlag[]): string {
  return composeFlagLines(openFlags(flags), '\n');
}
